// The blueprint's OGS composite, read ONCE into the value every pipeline method consumes.
// Notes: ~/comments/aos-threejs-poc/renderPlan_resolveRenderPlan_one_reader.md

import { DEFAULT_OGS_PRECISION, type OgsPrecision } from "./precision.ts";

export const NEXT_AVATAR_TYPE = "plugin.ogs.nextAvatar";
/** The inner graph's rig source — the same `entry` node a state machine's inner graph has. */
export const INNER_ENTRY_TYPE = "entry";
export const PART_TYPE = "plugin.ogs.part";
export const FACE_DRIVE_TYPE = "plugin.ogs.faceDrive";
export const BODY_DRIVE_TYPE = "plugin.ogs.bodyDrive";

/** The graph shapes this reads. Structural, so a test needs no editor. */
export interface GraphNode {
  id: string;
  type?: string;
  data?: Record<string, unknown> | null;
}

export interface GraphEdge {
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

/** `three` no composite · `created` present but starved · `rendering` the chain is live. */
export type RenderPhase = "three" | "created" | "rendering";

export type PlannedVertexSource = "auto" | "rig2mesh" | "orl" | "orl-joints" | "part-skin" | "static";
export type PartDecode = "auto" | "frozen" | "per-frame";
export type PartRecolour = "settle" | "always" | "never";
export type PartMeshView = "off" | "wire" | "solid" | "both";

export interface PartPlan {
  region: string;
  enabled: boolean;
  /** Why it is off, when it is — a part that quietly stops rendering reads as a broken bundle. */
  reason: string | null;
  vertexSource: PlannedVertexSource;
  decode: PartDecode;
  recolour: PartRecolour;
  gpuSkin: boolean;
  meshView: PartMeshView;
  lookAtCamera: boolean;
  /** Shade this region's MATERIAL, when its appr emits one. Off renders the
   *  albedo — see ogs_shade.wgsl's `lit`. Meaningless on an RGB region (hair),
   *  which has no material to shade either way. */
  pbr: boolean;
}

export type FaceRigMode = "auto" | "rig2mesh" | "orl-skin" | "orl-joints" | "off";
export type FaceExpression = "auto" | "trunk" | "local-disp" | "frozen";
export type Tristate = "auto" | "on" | "off";

/** ARKit channel groups the face may drive. False holds that group at REST. */
export interface LiveMask {
  brow: boolean;
  eye: boolean;
  cheek: boolean;
  nose: boolean;
}

export interface FaceDriveRequest {
  mode: FaceRigMode;
  expression: FaceExpression;
  syntheticFace: Tristate;
  liveMask: LiveMask;
}

export type BodyDriveSource = "blueprint" | "preview-only" | "none";

export interface BodyDrivePlan {
  enabled: boolean;
  source: BodyDriveSource;
  rootMotion: boolean;
}

export interface RenderPlan {
  phase: RenderPhase;
  precision: OgsPrecision;
  /** Keyed by region. A region with no part node is ABSENT, which means "render it". */
  parts: Record<string, PartPlan>;
  faceDrive: FaceDriveRequest;
  bodyDrive: BodyDrivePlan;
  /** Regions named by more than one part node — an authoring mistake, reported not merged. */
  duplicateRegions: string[];
  /** The composite's node id, for a message that can point at it. */
  compositeId: string | null;
}

/** GraphEvaluator's plugin convention: a node is active unless switched off or zero-weighted. */
function nodeActive(node: GraphNode | undefined | null): boolean {
  if (!node) return false;
  const data = node.data ?? {};
  return (data.enabled ?? true) !== false && Number(data.weight ?? 1) > 0;
}

const str = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  (typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : fallback);

const bool = (value: unknown, fallback: boolean): boolean => (typeof value === "boolean" ? value : fallback);

const VERTEX_SOURCES: readonly PlannedVertexSource[] = ["auto", "rig2mesh", "orl", "orl-joints", "part-skin", "static"];
const DECODES: readonly PartDecode[] = ["auto", "frozen", "per-frame"];
const RECOLOURS: readonly PartRecolour[] = ["settle", "always", "never"];
const MESH_VIEWS: readonly PartMeshView[] = ["off", "wire", "solid", "both"];
const FACE_MODES: readonly FaceRigMode[] = ["auto", "rig2mesh", "orl-skin", "orl-joints", "off"];
const EXPRESSIONS: readonly FaceExpression[] = ["auto", "trunk", "local-disp", "frozen"];
const TRISTATES: readonly Tristate[] = ["auto", "on", "off"];
const BODY_SOURCES: readonly BodyDriveSource[] = ["blueprint", "preview-only", "none"];
const PRECISIONS: readonly OgsPrecision[] = ["fp16", "fp32"];

/** What an untouched composite means: the bundle's own choices, everywhere.
 *  A fresh object per call, so no caller can mutate the default out from under the next. */
export function defaultFaceDrive(): FaceDriveRequest {
  return { mode: "auto", expression: "auto", syntheticFace: "auto",
    liveMask: { brow: true, eye: true, cheek: true, nose: true } };
}

/** No body-drive node is today's behaviour: the body blueprint may take the part rig. */
export function defaultBodyDrive(): BodyDrivePlan {
  return { enabled: true, source: "blueprint", rootMotion: true };
}

/** A graph with no composite: nothing OGS loads, and the phase says so. */
export function noCompositePlan(): RenderPlan {
  return { phase: "three", precision: DEFAULT_OGS_PRECISION, parts: {},
    faceDrive: defaultFaceDrive(), bodyDrive: defaultBodyDrive(), duplicateRegions: [], compositeId: null };
}

const asNodes = (value: unknown): GraphNode[] => (Array.isArray(value) ? (value as GraphNode[]) : []);
const asEdges = (value: unknown): GraphEdge[] => (Array.isArray(value) ? (value as GraphEdge[]) : []);

/** Is `pin` on `nodeId` fed by an active node in this graph? */
function wired(nodeId: string, pin: string, nodes: GraphNode[], edges: GraphEdge[]): boolean {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return edges.some((e) => e.target === nodeId && e.targetHandle === pin && nodeActive(byId.get(e.source)));
}

function partFrom(node: GraphNode, inner: GraphNode[], innerEdges: GraphEdge[]): PartPlan {
  const data = node.data ?? {};
  const active = nodeActive(node);
  // A part whose rig is unwired decodes nothing, so it renders nothing. Named, never quiet.
  // The rig reaches the inner graph through its `entry` node, as a state machine's does.
  const fed = wired(node.id, "rig", inner, innerEdges);
  return {
    region: typeof data.region === "string" && data.region ? data.region : "head",
    enabled: active && fed,
    reason: active ? (fed ? null : "its rig input is not wired inside the composite") : "switched off on its node",
    vertexSource: str(data.vertexSource, VERTEX_SOURCES, "auto"),
    decode: str(data.decode, DECODES, "auto"),
    recolour: str(data.recolour, RECOLOURS, "settle"),
    gpuSkin: bool(data.gpuSkin, true),
    meshView: str(data.meshView, MESH_VIEWS, "off"),
    lookAtCamera: bool(data.lookAtCamera, true),
    pbr: bool(data.pbr, true),
  };
}

function faceDriveFrom(node: GraphNode | undefined): FaceDriveRequest {
  if (!node || !nodeActive(node)) return defaultFaceDrive();
  const data = node.data ?? {};
  return {
    mode: str(data.mode, FACE_MODES, "auto"),
    expression: str(data.expression, EXPRESSIONS, "auto"),
    syntheticFace: str(data.syntheticFace, TRISTATES, "auto"),
    liveMask: {
      brow: bool(data.maskBrow, true),
      eye: bool(data.maskEye, true),
      cheek: bool(data.maskCheek, true),
      nose: bool(data.maskNose, true),
    },
  };
}

function bodyDriveFrom(node: GraphNode | undefined): BodyDrivePlan {
  if (!node) return defaultBodyDrive();
  const data = node.data ?? {};
  const active = nodeActive(node);
  return {
    enabled: active && bool(data.enabled, true),
    source: active ? str(data.source, BODY_SOURCES, "blueprint") : "none",
    rootMotion: bool(data.rootMotion, true),
  };
}

/** The whole render decision the graph owns. Pure; memoise on the nodes/edges identity. */
export function resolveRenderPlan(nodes: unknown, edges: unknown): RenderPlan {
  const top = asNodes(nodes);
  const topEdges = asEdges(edges);
  const composite = top.find((n) => n.type === NEXT_AVATAR_TYPE);
  if (!composite) return noCompositePlan();

  const data = composite.data ?? {};
  const inner = asNodes(data.innerNodes);
  const innerEdges = asEdges(data.innerEdges);

  const parts: Record<string, PartPlan> = {};
  const duplicateRegions: string[] = [];
  for (const node of inner) {
    if (node.type !== PART_TYPE) continue;
    const plan = partFrom(node, inner, innerEdges);
    // First node wins and the clash is REPORTED: two parts on one region is a
    // mistake, and merging them would hide which card is being edited.
    if (parts[plan.region]) { duplicateRegions.push(plan.region); continue; }
    parts[plan.region] = plan;
  }

  // The composite is the terminal: it renders when it is on and its rig is fed.
  const live = nodeActive(composite) && wired(composite.id, "rig", top, topEdges);
  return {
    phase: live ? "rendering" : "created",
    precision: str(data.precision, PRECISIONS, DEFAULT_OGS_PRECISION),
    parts,
    faceDrive: faceDriveFrom(inner.find((n) => n.type === FACE_DRIVE_TYPE)),
    bodyDrive: bodyDriveFrom(inner.find((n) => n.type === BODY_DRIVE_TYPE)),
    duplicateRegions,
    compositeId: composite.id,
  };
}

/** Is this region rendered? A region with NO part node is rendered — absence is not "off". */
export function partEnabled(plan: RenderPlan, region: string): boolean {
  return plan.parts[region]?.enabled ?? true;
}

/** Shade this region's material? A region with NO part node shades — absence is
 *  not "off", the same rule `partEnabled` follows. */
export function partPbr(plan: RenderPlan, region: string): boolean {
  return plan.parts[region]?.pbr ?? true;
}

/** The composite card's summary line. */
export function describeRenderPlan(plan: RenderPlan): string {
  if (plan.phase === "three") return "no OGS Avatar node — nothing renders this splat character";
  const named = Object.values(plan.parts);
  const on = named.filter((p) => p.enabled).length;
  const parts = named.length ? `${on}/${named.length} parts` : "every part (none named)";
  const face = plan.faceDrive.mode === "auto" && plan.faceDrive.expression === "auto"
    ? "face auto" : `face ${plan.faceDrive.mode}/${plan.faceDrive.expression}`;
  const body = plan.bodyDrive.enabled ? `body ${plan.bodyDrive.source}` : "body off";
  return `${parts} · ${face} · ${body} · ${plan.precision}${plan.phase === "created" ? " · rig not wired" : ""}`;
}
